import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { usePermission } from '../../context/PermissionContext';
import { getGstSettings, getSiteSettings, updateGstSettings, updateSiteSettings } from '../../services/api';

const getResponseData = (res) => res?.data?.data || res?.data?.settings || res?.data || {};
const getResponseMessage = (res, fallback) => res?.data?.message || res?.data?.data?.message || fallback;
const getErrorMessage = (err, fallback) => {
  const data = err?.response?.data || {};
  const firstError = Array.isArray(data.errors)
    ? data.errors[0]
    : data.errors && typeof data.errors === 'object'
      ? Object.values(data.errors).flat()[0]
      : '';
  return data.message || data.error || firstError || fallback;
};
const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '') || '';
const getGstNumberFromResponse = (res) => {
  const data = getResponseData(res);
  return firstValue(data.gstNumber, data.gst?.gstNumber, data.settings?.gstNumber);
};
const formatINR = (val) => (val === '' || val === undefined || val === null || isNaN(Number(val))) ? '—' : '₹' + Number(val).toLocaleString('en-IN');

const Settings = () => {
  const { hasPermission } = usePermission();
  const canEdit = hasPermission('settings', 'edit');
  const [form, setForm] = useState({
    websiteName: '', websiteEmail: '', phoneNumber: '', address: '',
    gstNumber: '', whatsappNumber: '', whatsappEnabled: true,
  });
  const [freeShippingMinOrderValue, setFreeShippingMinOrderValue] = useState('');
  const [isEditingFreeShipping, setIsEditingFreeShipping] = useState(false);
  const [freeShippingInput, setFreeShippingInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});
  const emailRegex = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;
  const phoneRegex = /^[6-9]\d{9}$/;
  const addressRegex = /^[A-Za-z0-9 ,./#-]+$/;

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 3200);
  };

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setErrors({});
    try {
      const [settingsRes, gstRes] = await Promise.all([
        getSiteSettings().catch((e) => { console.error('site settings error:', e); return null; }),
        getGstSettings().catch(() => null),
      ]);
      const data = getResponseData(settingsRes);
      const gstVal = getGstNumberFromResponse(gstRes) || firstValue(data.gstNumber, data.gst?.gstNumber, data.gst, data.settings?.gstNumber);

      const nextForm = {
        websiteName: firstValue(data.websiteName, data.siteName, data.name, data.companyName),
        websiteEmail: firstValue(data.websiteEmail, data.contactEmail, data.email),
        phoneNumber: firstValue(data.phoneNumber, data.contactPhone, data.phone, data.mobile),
        address: firstValue(data.address, data.officeAddress, data.contactAddress),
        gstNumber: gstVal,
        whatsappNumber: firstValue(data.whatsappNumber, data.whatsAppNumber, data.whatsapp, data.contactPhone),
        whatsappEnabled: data.whatsappEnabled !== undefined ? Boolean(data.whatsappEnabled) : true,
      };
      setForm(nextForm);

      const shippingVal = data?.freeShippingMinOrderValue !== undefined && data?.freeShippingMinOrderValue !== null
        ? Number(data.freeShippingMinOrderValue)
        : (data?.freeShippingThreshold !== undefined && data?.freeShippingThreshold !== null ? Number(data.freeShippingThreshold) : '');
      setFreeShippingMinOrderValue(shippingVal);
      setFreeShippingInput(shippingVal !== '' ? String(shippingVal) : '');

      setErrors({
        websiteEmail: validateField('websiteEmail', nextForm.websiteEmail),
        phoneNumber: validateField('phoneNumber', nextForm.phoneNumber),
        address: validateField('address', nextForm.address),
      });
    } catch (err) {
      console.error('Settings fetch error:', err);
      showMsg('error', getErrorMessage(err, 'Failed to load settings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const updateField = (field, value) => {
    if (!canEdit) return;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const validateField = (field, value) => {
    const trimmed = String(value ?? '').trim();
    if (field === 'websiteEmail') {
      if (!trimmed) return 'Website email is required.';
      if (!emailRegex.test(trimmed)) return 'Enter a valid email address without spaces.';
    }
    if (field === 'phoneNumber') {
      if (!trimmed) return 'Phone number is required.';
      if (!/^\d+$/.test(trimmed)) return 'Phone number must contain digits only.';
      if (!phoneRegex.test(trimmed)) return 'Enter a valid 10-digit Indian mobile number.';
    }
    if (field === 'address') {
      if (!trimmed) return 'Address is required.';
      if (!addressRegex.test(trimmed)) return 'Use only letters, numbers, spaces, commas, periods, hyphens, /, and #.';
    }
    return '';
  };

  const validateGeneral = () => {
    const next = {
      websiteEmail: validateField('websiteEmail', form.websiteEmail),
      phoneNumber: validateField('phoneNumber', form.phoneNumber),
      address: validateField('address', form.address),
    };
    setErrors((prev) => ({ ...prev, ...next }));
    return Object.values(next).every((error) => !error);
  };

  const requireEdit = () => {
    if (canEdit) return true;
    showMsg('error', 'You do not have permission to edit settings.');
    return false;
  };

  const saveSiteSettings = async (section) => {
    if (!requireEdit()) return;
    if (section === 'general' && !validateGeneral()) {
      showMsg('error', 'Please fix the highlighted fields.');
      return;
    }
    setSavingSection(section);
    try {
      const payload = {
        contactEmail: form.websiteEmail.trim(),
        contactPhone: form.phoneNumber.trim(),
        address: form.address.trim(),
      };
      const res = await updateSiteSettings(payload);
      showMsg('success', getResponseMessage(res, 'Settings updated successfully.'));
      window.dispatchEvent(new Event('adminDataUpdated'));
    } catch (err) {
      showMsg('error', getErrorMessage(err, 'Failed to update settings.'));
    } finally {
      setSavingSection('');
    }
  };

  const saveGstSettings = async () => {
    if (!requireEdit()) return;
    setSavingSection('gst');
    try {
      const normalizedGstNumber = form.gstNumber.trim().toUpperCase().replace(/\s+/g, '');
      const res = await updateGstSettings({ gstNumber: normalizedGstNumber });
      setForm((prev) => ({ ...prev, gstNumber: getGstNumberFromResponse(res) || normalizedGstNumber }));
      showMsg('success', getResponseMessage(res, 'GST settings updated successfully.'));
      window.dispatchEvent(new Event('adminDataUpdated'));
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to update GST settings.');
      setErrors((prev) => ({ ...prev, gstNumber: message }));
      showMsg('error', message);
    } finally {
      setSavingSection('');
    }
  };

  
  const saveWhatsAppSettings = async () => {
    if (!requireEdit()) return;
    setSavingSection('whatsapp');
    try {
      const res = await updateSiteSettings({
        whatsappNumber: form.whatsappNumber.trim(),
        whatsappEnabled: Boolean(form.whatsappEnabled),
      });
      showMsg('success', getResponseMessage(res, 'WhatsApp settings updated successfully.'));
      window.dispatchEvent(new Event('adminDataUpdated'));
    } catch (err) {
      showMsg('error', getErrorMessage(err, 'Failed to update WhatsApp settings.'));
    } finally {
      setSavingSection('');
    }
  };

  const handleStartEditShipping = () => {
    if (!requireEdit()) return;
    setFreeShippingInput(String(freeShippingMinOrderValue));
    setIsEditingFreeShipping(true);
  };

  const handleCancelEditShipping = () => {
    setFreeShippingInput(String(freeShippingMinOrderValue));
    setIsEditingFreeShipping(false);
  };

  const saveFreeShipping = async () => {
    if (!requireEdit()) return;
    const num = Number(String(freeShippingInput).replace(/[^0-9]/g, ''));
    if (freeShippingInput === '' || isNaN(num) || num < 0) {
      showMsg('error', 'Please enter a valid free shipping minimum order value.');
      return;
    }
    setSavingSection('shipping');
    try {
      const res = await updateSiteSettings({ freeShippingMinOrderValue: num });
      const resData = getResponseData(res);
      const savedVal = resData?.freeShippingMinOrderValue !== undefined && resData?.freeShippingMinOrderValue !== null
        ? Number(resData.freeShippingMinOrderValue)
        : num;
      setFreeShippingMinOrderValue(savedVal);
      setFreeShippingInput(String(savedVal));
      setIsEditingFreeShipping(false);
      showMsg('success', getResponseMessage(res, 'Settings updated'));
      window.dispatchEvent(new Event('adminDataUpdated'));
    } catch (err) {
      showMsg('error', getErrorMessage(err, 'Failed to update settings.'));
    } finally {
      setSavingSection('');
    }
  };

  const css = `
    .settings-page{padding:18px 8px 36px;font-family:'DM Sans',sans-serif;color:#16233f !important;}
    .settings-head{display:flex;align-items:center;justify-content:space-between;gap:20px;margin-bottom:26px;}
    .settings-title-row{display:flex;align-items:center;gap:14px;}
    .settings-icon{width:46px;height:46px;border-radius:14px;display:flex;align-items:center;justify-content:center;background:#fff8e7;border:1px solid #f3d18a;color:#c58a20;box-shadow:0 8px 18px rgba(197,138,32,.12);}
    .settings-title{margin:0;color:#16233f !important;font-size:28px;font-weight:800;letter-spacing:-0.02em;}
    .settings-sub{margin-top:5px;color:#71809a !important;font-size:13.5px;font-weight:500;}
    .settings-lock{padding:8px 14px;border-radius:999px;background:#f4efff;border:1px solid #d9c7ff;font-size:12px;font-weight:700;color:#7444c7;white-space:nowrap;}
    .settings-msg{margin-bottom:18px;padding:14px 16px;border-radius:10px;font-size:13px;font-weight:700;}
    .settings-msg.success{background:#ecfbf4;border:1px solid #bcebd2;color:#13814c;}
    .settings-msg.error{background:#fff1f1;border:1px solid #f2c5c5;color:#b42318;}
    .settings-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:20px;}
    .settings-card{background:#ffffff !important;border:1px solid #e5eaf2 !important;border-radius:14px !important;box-shadow:0 14px 34px rgba(20,35,63,.08) !important;padding:24px;}
    .settings-card-wide{grid-column:1/-1;}
    .settings-card-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:22px;padding-bottom:15px;border-bottom:1px solid #edf0f5;}
    .settings-card h2{margin:0;color:#243451 !important;font-size:17px;font-weight:800;letter-spacing:0;}
    .settings-card p{margin:4px 0 0;color:#7c889b !important;font-size:12.5px;font-weight:500;}
    .settings-fields{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;}
    .settings-field{display:flex;flex-direction:column;gap:7px;min-width:0;}
    .settings-field.full{grid-column:1/-1;}
    .settings-field span{font-size:11px;letter-spacing:1.4px;text-transform:uppercase;font-weight:800;color:#bf861e;}
    .settings-input,.settings-textarea{width:100%;box-sizing:border-box;border:1px solid #d7dfeb;border-radius:10px;background:#fbfcfe;color:#263650;padding:12px 14px;font-family:'DM Sans',sans-serif;font-size:13.5px;outline:none;transition:all .2s;}
    .settings-input:focus,.settings-textarea:focus{border-color:#8c5de8;box-shadow:0 0 0 3px rgba(140,93,232,.14);background:#ffffff;}
    .settings-input-error{border-color:#dc2626 !important;box-shadow:0 0 0 3px rgba(220,38,38,.12) !important;background:#fffafa !important;}
    .settings-input:read-only,.settings-textarea:read-only,.settings-input:disabled{background:#f5f7fa;color:#69778d;cursor:not-allowed;}
    .settings-textarea{min-height:94px;resize:vertical;}
    .settings-error{font-size:12px;color:#b42318;font-weight:700;}
    .settings-switches{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;}
    .settings-switch{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:16px;border:1px solid #312e81;border-radius:14px;background:rgba(10,15,31,.5);}
    .settings-switch strong{display:block;font-size:14px;color:#ffffff;}
    .settings-switch small{display:block;margin-top:3px;color:#c4b5fd;font-size:12px;font-weight:500;}
    .settings-toggle{position:relative;width:48px;height:26px;flex:0 0 auto;}
    .settings-toggle input{opacity:0;width:0;height:0;}
    .settings-toggle span{position:absolute;cursor:pointer;inset:0;background:#312e81;border-radius:999px;transition:.18s;}
    .settings-toggle span:before{content:'';position:absolute;width:20px;height:20px;left:3px;top:3px;border-radius:50%;background:#fff;transition:.18s;box-shadow:0 2px 6px rgba(0,0,0,.3);}
    .settings-toggle input:checked + span{background:linear-gradient(135deg,#6d28d9,#a855f7);}
    .settings-toggle input:checked + span:before{transform:translateX(22px);}
    .settings-toggle input:disabled + span{opacity:.5;cursor:not-allowed;}
    .settings-actions{display:flex;justify-content:flex-end;margin-top:20px;}
    .settings-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:42px;padding:10px 22px;border:none;border-radius:10px;background:linear-gradient(135deg,#6d28d9,#9b57e8);color:#fff;font-family:'DM Sans',sans-serif;font-size:13.5px;font-weight:800;cursor:pointer;box-shadow:0 8px 18px rgba(109,40,217,.22);transition:all .2s;}
    .settings-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 12px 24px rgba(109,40,217,.3);}
    .settings-btn:disabled{opacity:.5;cursor:not-allowed;transform:none;}
    .settings-spinner{width:15px;height:15px;border:2px solid rgba(255,255,255,.45);border-top-color:#fff;border-radius:50%;animation:settingsSpin .75s linear infinite;display:inline-block;}
    .settings-loader{height:360px;display:flex;align-items:center;justify-content:center;}
    .settings-loader span{width:34px;height:34px;border:3px solid rgba(217,154,43,.24);border-top-color:#d99a2b;border-radius:50%;animation:settingsSpin .75s linear infinite;}
    
    .settings-shipping-box{display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;box-sizing:border-box;border:1px solid #d7dfeb;border-radius:10px;background:#fbfcfe;color:#263650;padding:6px 10px 6px 14px;min-height:45px;transition:all .2s;}
    .settings-shipping-box.is-editing{border-color:#8c5de8;box-shadow:0 0 0 3px rgba(140,93,232,.14);background:#ffffff;}
    .settings-shipping-val{font-size:14px;font-weight:600;color:#263650;letter-spacing:0.2px;}
    .settings-shipping-input-wrap{display:flex;align-items:center;gap:4px;flex:1;min-width:0;}
    .settings-currency-prefix{font-size:14px;font-weight:700;color:#6b7280;}
    .settings-shipping-input{border:none;background:transparent;outline:none;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:600;color:#16233f;width:100%;padding:4px 0;}
    .settings-shipping-actions{display:flex;align-items:center;gap:6px;flex-shrink:0;}
    .settings-edit-outline-btn{display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border:1px solid #d9c7ff;border-radius:8px;background:#ffffff;color:#6d28d9;font-family:'DM Sans',sans-serif;font-size:12.5px;font-weight:700;cursor:pointer;transition:all .18s;box-shadow:0 1px 3px rgba(109,40,217,.06);}
    .settings-edit-outline-btn:hover{background:#f8f5ff;border-color:#bda4ff;color:#5b21b6;transform:translateY(-1px);}
    .settings-cancel-btn{padding:6px 12px;border:1px solid #d7dfeb;border-radius:8px;background:#ffffff;color:#64748b;font-family:'DM Sans',sans-serif;font-size:12px;font-weight:700;cursor:pointer;transition:all .18s;}
    .settings-cancel-btn:hover:not(:disabled){background:#f1f5f9;color:#334155;}
    .settings-save-sm-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;padding:6px 14px;border:none;border-radius:8px;background:linear-gradient(135deg,#6d28d9,#9b57e8);color:#ffffff;font-family:'DM Sans',sans-serif;font-size:12px;font-weight:700;cursor:pointer;box-shadow:0 4px 10px rgba(109,40,217,.2);transition:all .18s;}
    .settings-save-sm-btn:hover:not(:disabled){background:linear-gradient(135deg,#5b21b6,#8c5de8);transform:translateY(-1px);}
    .settings-save-sm-btn:disabled,.settings-cancel-btn:disabled{opacity:.5;cursor:not-allowed;}
    .settings-info-alert{display:flex;align-items:flex-start;gap:10px;margin-top:16px;padding:12px 14px;border-radius:10px;background:#eff6ff;border:1px solid #dbeafe;}
    .settings-info-icon{flex-shrink:0;margin-top:2px;}
    .settings-info-text{font-size:12px;line-height:1.55;color:#334155;font-weight:500;}

    @keyframes settingsSpin{to{transform:rotate(360deg)}}
    @media(max-width:900px){.settings-grid,.settings-fields,.settings-switches{grid-template-columns:1fr}.settings-head{align-items:flex-start;flex-direction:column}.settings-title{font-size:24px}.settings-lock{white-space:normal}}
  `;

  const inputProps = { readOnly: !canEdit };
  const generalHasErrors = ['websiteEmail', 'phoneNumber', 'address'].some((field) => !!errors[field]);
  const savingLabel = (section, label) => savingSection === section ? <><span className="settings-spinner" />Saving...</> : label;

  return (
    <AdminLayout>
      <style>{css}</style>
      <div className="settings-page">
        <div className="settings-head">
          <div className="settings-title-row">
            <div className="settings-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
            </div>
            <div><h1 className="settings-title">Settings</h1><div className="settings-sub">Manage storefront and tax information</div></div>
          </div>
          <div className="settings-lock">{canEdit ? 'Editing enabled' : 'View only'}</div>
        </div>

        {msg.text && <div className={'settings-msg ' + msg.type}>{msg.text}</div>}

        {loading ? <div className="settings-loader"><span /></div> : (
          <div className="settings-grid">
            <section className="settings-card settings-card-wide">
              <div className="settings-card-head"><div><h2>General Settings</h2><p>Business identity and customer-facing contact information</p></div></div>
              <div className="settings-fields">
                <label className="settings-field"><span>Website Email</span><input className={'settings-input' + (errors.websiteEmail ? ' settings-input-error' : '')} type="email" value={form.websiteEmail} onChange={(e) => updateField('websiteEmail', e.target.value)} onBlur={(e) => updateField('websiteEmail', e.target.value.trim())} {...inputProps} />{errors.websiteEmail && <em className="settings-error">{errors.websiteEmail}</em>}</label>
                <label className="settings-field"><span>Phone Number</span><input className={'settings-input' + (errors.phoneNumber ? ' settings-input-error' : '')} value={form.phoneNumber} onChange={(e) => updateField('phoneNumber', e.target.value)} onBlur={(e) => updateField('phoneNumber', e.target.value.trim())} {...inputProps} />{errors.phoneNumber && <em className="settings-error">{errors.phoneNumber}</em>}</label>
                <label className="settings-field full"><span>Address</span><textarea className={'settings-textarea' + (errors.address ? ' settings-input-error' : '')} value={form.address} onChange={(e) => updateField('address', e.target.value)} onBlur={(e) => updateField('address', e.target.value.trim())} {...inputProps} />{errors.address && <em className="settings-error">{errors.address}</em>}</label>
              </div>
              <div className="settings-actions"><button type="button" className="settings-btn" disabled={!canEdit || !!savingSection || generalHasErrors} onClick={() => saveSiteSettings('general')}>{savingLabel('general', 'Save General Settings')}</button></div>
            </section>


            <section className="settings-card settings-card-wide">
              <div className="settings-card-head">
                <div>
                  <h2>WhatsApp Support Settings</h2>
                  <p>Configure the floating WhatsApp contact button for website visitors</p>
                </div>
              </div>
              <div className="settings-fields">
                <label className="settings-field">
                  <span>WhatsApp Number</span>
                  <input
                    className="settings-input"
                    placeholder="e.g. 9665315655"
                    value={form.whatsappNumber || ''}
                    onChange={(e) => updateField('whatsappNumber', e.target.value.replace(/[^0-9+]/g, ''))}
                    {...inputProps}
                  />
                </label>
                <div className="settings-field">
                  <span>WhatsApp Button Status</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', cursor: canEdit ? 'pointer' : 'not-allowed' }}>
                    <input
                      type="checkbox"
                      checked={!!form.whatsappEnabled}
                      onChange={(e) => updateField('whatsappEnabled', e.target.checked)}
                      disabled={!canEdit}
                      style={{ width: '18px', height: '18px', accentColor: '#25D366' }}
                    />
                    <strong style={{ fontSize: '13.5px', color: form.whatsappEnabled ? '#13814c' : '#b42318' }}>
                      {form.whatsappEnabled ? 'Enabled (Button Visible on Website)' : 'Disabled (Button Hidden on Website)'}
                    </strong>
                  </label>
                </div>
              </div>
              <div className="settings-actions">
                <button
                  type="button"
                  className="settings-btn"
                  disabled={!canEdit || !!savingSection}
                  onClick={saveWhatsAppSettings}
                >
                  {savingLabel('whatsapp', 'Save WhatsApp Settings')}
                </button>
              </div>
            </section>

            <section className="settings-card">
              <div className="settings-card-head"><div><h2>GST Settings</h2><p>Tax registration used on invoices</p></div></div>
              <label className="settings-field"><span>GST Number</span><input className="settings-input" value={form.gstNumber} maxLength={15} onChange={(e) => updateField('gstNumber', e.target.value.toUpperCase().replace(/\s+/g, ''))} {...inputProps} />{errors.gstNumber && <em className="settings-error">{errors.gstNumber}</em>}</label>
              <div className="settings-actions"><button type="button" className="settings-btn" disabled={!canEdit || !!savingSection} onClick={saveGstSettings}>{savingLabel('gst', 'Save GST')}</button></div>
            </section>

            <section className="settings-card">
              <div className="settings-card-head">
                <div>
                  <h2>Free Shipping Settings</h2>
                  <p>Minimum order value for free shipping</p>
                </div>
              </div>
              <div className="settings-field">
                <span>Free Shipping Minimum Order Value</span>
                <div className={'settings-shipping-box' + (isEditingFreeShipping ? ' is-editing' : '')}>
                  {isEditingFreeShipping ? (
                    <>
                      <div className="settings-shipping-input-wrap">
                        <span className="settings-currency-prefix">₹</span>
                        <input
                          type="text"
                          className="settings-shipping-input"
                          value={freeShippingInput}
                          onChange={(e) => setFreeShippingInput(e.target.value.replace(/[^0-9]/g, ''))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveFreeShipping();
                            if (e.key === 'Escape') handleCancelEditShipping();
                          }}
                          placeholder="Enter amount"
                          autoFocus
                          disabled={savingSection === 'shipping'}
                        />
                      </div>
                      <div className="settings-shipping-actions">
                        <button
                          type="button"
                          className="settings-cancel-btn"
                          onClick={handleCancelEditShipping}
                          disabled={savingSection === 'shipping'}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="settings-save-sm-btn"
                          onClick={saveFreeShipping}
                          disabled={savingSection === 'shipping' || !freeShippingInput}
                        >
                          {savingSection === 'shipping' ? (
                            <><span className="settings-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />Saving...</>
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="settings-shipping-val">{formatINR(freeShippingMinOrderValue)}</span>
                      {canEdit && (
                        <button
                          type="button"
                          className="settings-edit-outline-btn"
                          onClick={handleStartEditShipping}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Edit
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="settings-info-alert">
                <svg className="settings-info-icon" width="18" height="18" viewBox="0 0 20 20" fill="#2563eb">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="settings-info-text">
                  <div>Orders above {formatINR(freeShippingMinOrderValue)} will get free shipping.</div>
                  <div style={{ marginTop: '3px' }}>For orders below {formatINR(freeShippingMinOrderValue)}, shipping charges will apply.</div>
                </div>
              </div>
            </section>

          </div>
        )}
      </div>
    </AdminLayout>
  );
};


export default Settings;
